import {
	CheckboxField,
	DrawerBody,
	DrawerFooter,
	DrawerFooterActions,
	I18nDiscardLocaleButton,
	I18nOnlyOnMainForm,
	I18nTextField,
	MultiSelectField,
	SubmitButton,
	SupportedLanguageSingleSelectField,
	TextField,
} from "@dgs/core";
import { Form, useFormikContext } from "formik";
import { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { visibilityOptions } from "../../../shared/types/event";
import { EventRoleSingleSelectField } from "../shared/select/EventRoleSingleSelectField";
import { EventRole, EventTypeFormValues } from "../shared/types/eventType";
import { EventTypeImagePicker } from "./EventTypeImagePicker";
import { FeedbackMultiSelectField } from "./FeedbackMultiSelectField";

interface Props {
	onSubmitted: () => void;
	unsavedChangesPrompt: ReactNode;
}

const StyledForm = styled(Form)`
	display: flex;
	flex-direction: column;
	overflow: hidden;
	height: 100%;
`;

export const EventTypeBaseForm: FC<Props> = ({ unsavedChangesPrompt }) => {
	const { t } = useTranslation();
	const { values, setFieldValue } = useFormikContext<EventTypeFormValues>();

	return (
		<StyledForm>
			<DrawerBody>
				{unsavedChangesPrompt}
				<I18nTextField name="name" label={t("Name")} required="onlyOnMainForm" />
				<I18nOnlyOnMainForm>
					<EventRoleSingleSelectField
						label={t("Event role")}
						name="eventRole"
						required={true}
						description={t("Determines the role our company has at this event.")}
						onChange={(event) =>
							event.target.value !== EventRole.ATTENDEE && setFieldValue("requiresParticipationApproval", false)
						}
					/>
					{values.eventRole === EventRole.ATTENDEE && (
						<CheckboxField
							label={t("Enable participant approval")}
							name="requiresParticipationApproval"
							infoText={t("When enabled, each participant must be approved before joining.")}
						/>
					)}
					<SupportedLanguageSingleSelectField label={t("Fallback language")} name="fallbackLanguage" required />
					<FeedbackMultiSelectField name="feedback" label={t("Feedback")} clearable />
					<TextField type="color" name="color" label={t("Color")} />
				</I18nOnlyOnMainForm>
				<EventTypeImagePicker label={t("Image")} name="image" />
				<I18nOnlyOnMainForm>
					<CheckboxField label={t("Mark as default")} name="isDefault" />
					<CheckboxField label={t("All users can edit events of this type")} name="isEventEditableByAllUsers" />
					<CheckboxField label={t("Show feedback evaluation")} name="showFeedbackEvaluation" />
					<MultiSelectField
						name="allowedVisibilities"
						label={t("Visibility")}
						required={true}
						options={visibilityOptions}
						getLabel={(v) => t(`eventVisibility.${v}`)}
						getValue={(v) => v}
					/>
				</I18nOnlyOnMainForm>
			</DrawerBody>
			<DrawerFooter>
				<DrawerFooterActions>
					<I18nDiscardLocaleButton />
				</DrawerFooterActions>
				<SubmitButton title={t("Continue")}>{t("Continue")}</SubmitButton>
			</DrawerFooter>
		</StyledForm>
	);
};
